import React, { useState, useEffect, useCallback, useRef, useImperativeHandle, forwardRef, Fragment } from 'react';
import { DataGrid, Column, FilterRow, Scrolling, DataGridRef, Editing, Lookup, DataGridTypes } from 'devextreme-react/data-grid';
import { DataChange } from 'devextreme/common/grids';
import { Button, CheckBox, DropDownBox } from 'devextreme-react';
import TreeView from 'devextreme-react/tree-view';

import { formatMessage } from 'devextreme/localization';

import { useAuth } from '../../contexts/auth';

import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import GlobalLoader from '../loader/GlobalLoader';

import FieldTypeTag from '../field-type-tag/FieldTypeTag';
import TargetFieldTypeCell from '../field-type-tag/FieldTypeCell';
import useTransformationColumn from '../transformation-popup/useTransformationColumn';
import useFilterColumn from '../filter-popup/useFilterColumn';
import StringHelper from '../../tools/StringHelper';

import PathSynchroApi from '../../api/PathSynchroApi';

import { TransformationLogic } from '../../logics/transformationmodule/TransformationLogic';

import PathSynchroMappingCapabilitiesDto from '../../classes/dtos/pathsynchromodule/PathSynchroMappingCapabilitiesDto';

import FieldReferenceIdAndKeyDto from '../../classes/dtos/FieldReferenceIdAndKeyDto';
import TransformationTypeDto from '../../classes/dtos/pathcommonmodule/TransformationTypeDto';
import TransformationCapabilitiesDto from '../../classes/dtos/transformationmodule/TransformationCapabilitiesDto';

import SynchroFieldMappingDto from '../../classes/dtos/pathsynchromodule/SynchroFieldMappingDto';

import './FieldGrid.css';

interface FieldGridProps {
    PathId: number;
    MappingChanges: DataChange[] | undefined;

    SourceFields: FieldReferenceIdAndKeyDto[];
    TargetFields: FieldReferenceIdAndKeyDto[];
    Capabilities: PathSynchroMappingCapabilitiesDto[];
    TransformationsCapabilities: TransformationCapabilitiesDto[];
    Mapping: SynchroFieldMappingDto[];
    IsSimpleFilterMode: boolean;
    // Colonne FILTER affichée seulement quand true : feature Feature.FilterVisibility active pour le client
    // ET table source qui gère le filtrage (même calcul `withFilter` que FiltersSelection). Calculé côté page.
    FiltersEnabled: boolean;
    TableReferenceSourceId: number | null;
    PathSourceId: number | null;
    TableReferenceTargetId: number;

    AdvancedFilterMode: boolean;
    SetAdvancedFilterMode(value: boolean): void;
    AdvancedFilterModeLocked: boolean;

    SetMappingChanges(changes: DataChange[], origin: SynchroFieldMappingDto[]): void;
}

export interface FieldGridHandle {
    Save: () => void;
}

interface GridBodyProps {
    dataGridRef: React.RefObject<DataGridRef>;
    dataSource: DataSource;
    onEditorPreparing: (e: DataGridTypes.EditorPreparingEvent) => void;
    onEditingStart: (e: DataGridTypes.EditingStartEvent) => void;
    onRowPrepared: (e: DataGridTypes.RowPreparedEvent) => void;
    onEditingChangesChange: (e: DataChange[]) => void;
    renderUnlinkCommand: (cellData: DataGridTypes.ColumnCellTemplateData) => JSX.Element;
    renderColumnHeader: () => JSX.Element;
    handleSourceFieldReferenceIdCalculateDisplayValue: (rowData: any) => string;
    selectFieldCell: (cellData: DataGridTypes.ColumnCellTemplateData) => JSX.Element;
    renderSourceFieldTreeView: (cellData: DataGridTypes.ColumnEditCellTemplateData) => JSX.Element;
    sourceFieldTypeCell: (cellData: DataGridTypes.ColumnCellTemplateData) => JSX.Element;
    transformationButtonCell: (cellData: DataGridTypes.ColumnCellTemplateData) => JSX.Element;
    transformationCalculateCellValue: (rowData: SynchroFieldMappingDto) => boolean;
    transformationCalculateSortValue: (rowData: SynchroFieldMappingDto) => string;
    renderKeyCell: (cellData: DataGridTypes.ColumnCellTemplateData) => JSX.Element;
    filterColumnLeaf: JSX.Element;
    FiltersEnabled: boolean;
    IsSimpleFilterMode: boolean;
}

// Isolated from FieldGrid and memoized so the DataGrid tree is untouched by a popup-only re-render:
// useTransformationColumn()/useFilterColumn() own their `popup` visibility state, but since those hooks
// are called directly in FieldGrid's body, that state lives on FieldGrid's own fiber — any setPopup(...)
// re-renders FieldGrid as a whole. Without this memo boundary, React would still re-run every Column's
// render (and DevExtreme would re-diff every row's cell templates) purely because the parent re-rendered,
// even though every prop below is already reference-stable. The button-cell/calculate* callbacks read
// their live data through refs (see transformationColumnRef / filterColumnRef below), so they stay
// correct even though this component intentionally skips re-rendering when only a popup toggles.
const GridBody = React.memo(({ dataGridRef, dataSource, onEditorPreparing, onEditingStart, onRowPrepared, onEditingChangesChange, renderUnlinkCommand, renderColumnHeader, handleSourceFieldReferenceIdCalculateDisplayValue, selectFieldCell, renderSourceFieldTreeView, sourceFieldTypeCell, transformationButtonCell, transformationCalculateCellValue, transformationCalculateSortValue, renderKeyCell, filterColumnLeaf, FiltersEnabled, IsSimpleFilterMode }: GridBodyProps) => (
    <DataGrid
        id="dataGrid"
        ref={dataGridRef}
        dataSource={dataSource}
        columnAutoWidth={false}
        height='calc(100vh - 340px)'
        onEditorPreparing={onEditorPreparing}
        onEditingStart={onEditingStart}
        onRowPrepared={onRowPrepared}
    >
        <Scrolling mode="infinite" />
        <FilterRow visible={true} />

        <Editing
            mode="batch"
            allowUpdating={true}
            allowAdding={false}
            allowDeleting={false}
            refreshMode="repaint"
            onChangesChange={onEditingChangesChange}
        />

        <Column width={70} cellRender={renderUnlinkCommand} allowEditing={false} headerCellRender={renderColumnHeader} />
        <Column caption="SOURCE" cssClass="GridGroupHeader">
            <Column caption="FIELD NAME" dataField="SourceFieldReferenceId" dataType="number" alignment="left" allowEditing={true} calculateDisplayValue={handleSourceFieldReferenceIdCalculateDisplayValue} cellRender={selectFieldCell} editCellRender={renderSourceFieldTreeView} />
            <Column caption="FIELD TYPE" dataField="SourceFieldTypeLabel" dataType="string" allowEditing={false}
                cellRender={sourceFieldTypeCell}
                filterOperations={["contains"]}
                width={200}
            />
        </Column>
            <Column caption="TRANSFORMATION" dataType="boolean" allowEditing={false} allowFiltering={true} width={200} cssClass="GridGroupHeader transformation-cell-td"
                cellRender={transformationButtonCell}
                calculateCellValue={transformationCalculateCellValue}
                calculateSortValue={transformationCalculateSortValue}
                trueText={formatMessage("Grid_ColumnFilterWith")}
                falseText={formatMessage("Grid_ColumnFilterWithout")}
            />
            <Column dataField="TransformationType" visible={false} allowEditing={true} />
            <Column dataField="TransformationParameters" visible={false} allowEditing={true} />
        <Column caption="TARGET" cssClass="GridGroupHeader">
            <Column caption="FIELD NAME" dataField="Name" dataType="string" allowEditing={false} />
            <Column caption="FIELD TYPE" dataField="TypeLabel" dataType="string" allowEditing={false}
                cellRender={TargetFieldTypeCell}
                filterOperations={["contains"]}
                width={200}
            />
        </Column>
        <Column caption="KEY" dataField="IsKey" dataType="boolean" alignment="center" allowEditing={false} width="100px" cssClass="GridGroupHeader" cellRender={renderKeyCell} />
        {FiltersEnabled && (IsSimpleFilterMode ? (
            <Column caption="FILTER" cssClass="GridGroupHeader">
                {filterColumnLeaf}
            </Column>
        ) : filterColumnLeaf)}

        <Column dataField="Filters" visible={false} allowEditing={true} />
        <Column dataField="DeclaredVariables" visible={false} allowEditing={true} />
    </DataGrid>
));

const FieldGrid = forwardRef<FieldGridHandle, FieldGridProps>(({ PathId, MappingChanges, SetMappingChanges, SourceFields, TargetFields, Capabilities, TransformationsCapabilities, Mapping, IsSimpleFilterMode, FiltersEnabled, TableReferenceSourceId, PathSourceId, TableReferenceTargetId, AdvancedFilterMode, SetAdvancedFilterMode, AdvancedFilterModeLocked }, ref) => {

    const dataGridRef = useRef<DataGridRef>(null);
    const mappingRef = useRef<SynchroFieldMappingDto[]>([]);
    const storeRef = useRef<CustomStore<any>>();
    const dataSourceRef = useRef<DataSource>();

    // storeRef.onLoaded runs long after the store was created; read the current prop through a ref so a
    // reload after save (when the parent has cleared its pending changes) doesn't re-inject stale edits.
    const mappingChangesRef = useRef<DataChange[] | undefined>(MappingChanges);
    mappingChangesRef.current = MappingChanges;

    const { sessionDetail } = useAuth();

    const [mapping, setMapping] = useState<SynchroFieldMappingDto[]>(Mapping);

    useEffect(() => {
        setMapping(Mapping);
    }, [Mapping]);

    useEffect(() => {
        mappingRef.current = mapping ?? [];
        dataSourceRef.current?.reload();
    }, [mapping]);

    // The unlink/link icon, the source-field label and the source FIELD TYPE cell are calculated
    // cellRender columns: DevExtreme doesn't reliably repaint them when SourceFieldReferenceId is
    // changed via cellValue() on another column in batch mode. Read the live batch value from the grid
    // (cellValue() returns the pending change in batch mode) and repaint the row after each write —
    // same single-source-of-truth approach as the transformation button.
    const getEffectiveSourceFieldReferenceId = useCallback((cellData: DataGridTypes.ColumnCellTemplateData): number | null => {
        const rowIndex = cellData.rowIndex ?? cellData.row?.rowIndex;
        const rowData = cellData.data as SynchroFieldMappingDto;
        const hasGridHandle =
            cellData.component && rowIndex !== undefined && rowIndex !== null && rowIndex >= 0 &&
            // During virtual scrolling DevExtreme can call cellRender for a recycled row before its
            // internal cellValue cache has caught up with the row now at this index; cellValue would
            // then briefly answer with another row's (or no) value — e.g. null for a row that IS
            // linked, which flashed the unlink icon / TRANSFORMATION button red. Guard against it by
            // checking the row key still matches (mirrors useTransformationColumn's own guard).
            cellData.component.cellValue(rowIndex, 'TargetFieldReferenceId') === rowData?.TargetFieldReferenceId;

        if (hasGridHandle) {
            return cellData.component.cellValue(rowIndex, 'SourceFieldReferenceId') ?? null;
        }
        return rowData?.SourceFieldReferenceId ?? null;
    }, []);

    const translatedSourceFields = React.useMemo(() => {
        return SourceFields.map(field => {
            if (field.Id === -1001) {
                return new FieldReferenceIdAndKeyDto(
                    field.Id,
                    field.SourceKey,
                    field.DatasetKey,
                    formatMessage("components_synchro-mapping-fields_FieldGrid_FieldAI"),
                    field.Type
                );
            }
            if (field.Id === -1002) {
                return new FieldReferenceIdAndKeyDto(
                    field.Id,
                    field.SourceKey,
                    field.DatasetKey,
                    formatMessage("components_synchro-mapping-fields_FieldGrid_FieldConst"),
                    field.Type
                );
            }
            return field;
        });
    }, [SourceFields]);

    type UpdateObject = { [key: string]: any };

    function updateObjectInPlace(target: UpdateObject, updates: UpdateObject): void {
        Object.assign(target, updates);
    }

    function getFields(targetFieldReferenceId: number, sourceFields: FieldReferenceIdAndKeyDto[], capabilities: PathSynchroMappingCapabilitiesDto[]): FieldReferenceIdAndKeyDto[] {
        let fields = new Array<FieldReferenceIdAndKeyDto>();
        sourceFields.forEach((sourceField) => {
            let currentCapabilities = capabilities.find((e) => e.TargetFieldId === targetFieldReferenceId) ?? null;
            if (currentCapabilities !== null) {
                let source = currentCapabilities.Capabilities.find((e) => e.SourceFieldId === sourceField.Id) ?? null;
                if (source !== null && source.TransformationLogicIds.length > 0) {
                    fields.push(sourceField);
                }
            }
        });
        return fields;
    }

    useImperativeHandle(ref, () => ({
        Save() {
            if (dataGridRef.current) {
                dataGridRef.current.instance().saveEditData();
            }
        }
    }));

    // Custom data source backed by refs, kept stable across renders so DevExtreme
    // doesn't treat every render as a brand-new dataSource and discard pending batch edits.
    if (!storeRef.current) {
        storeRef.current = new CustomStore({
            key: 'TargetFieldReferenceId',
            loadMode: 'raw',
            load: () => mappingRef.current,
            update: (key: any, values: SynchroFieldMappingDto): any => {

                let row = mappingRef.current.find(e => e.TargetFieldReferenceId === key) ?? null;

                if (row !== null) {
                    updateObjectInPlace(row, values);
                }
            },
            onLoaded: (): any => {
                if (dataGridRef.current?.instance() !== undefined) {
                    dataGridRef.current.instance().option('editing.changes', mappingChangesRef.current ?? []);
                }
            }
        });
    }

    if (!dataSourceRef.current) {
        dataSourceRef.current = new DataSource({
            store: storeRef.current,
            paginate: false,
            reshapeOnPush: true
        });
    }

    const calculateLookupTransformationTypes = useCallback((rowData: SynchroFieldMappingDto) => {

        let transformations = new Array<TransformationTypeDto>();

        if (rowData.SourceFieldReferenceId !== null) {
            let currentCapabilities = Capabilities.find((e) => e.TargetFieldId === rowData.TargetFieldReferenceId) ?? null;
            if (currentCapabilities !== null) {
                let source = currentCapabilities.Capabilities.find((e) => e.SourceFieldId === rowData.SourceFieldReferenceId) ?? null;
                if (source !== null && source.TransformationLogicIds.length > 0) {
                    TransformationsCapabilities.forEach((transformationCapabilities) => {
                        let found = source?.TransformationLogicIds.find((x) => x === transformationCapabilities.LogicId) ?? null;
                        if (found !== null && transformationCapabilities.LogicId !== 0) {
                            transformations.push(new TransformationTypeDto(transformationCapabilities.LogicId, transformationCapabilities.Label));
                        }
                    });
                }
            }
        }

        return transformations;
    }, [Capabilities, TransformationsCapabilities]);

    // calculateLookupTransformationTypes drops "Direct" (LogicId 0) from its list, so its length alone
    // can't tell whether Direct was also an allowed choice. Check the raw capability ids for that.
    const isDirectTransformationPossible = useCallback((rowData: SynchroFieldMappingDto): boolean => {
        if (rowData.SourceFieldReferenceId === null) {
            return false;
        }
        let currentCapabilities = Capabilities.find((e) => e.TargetFieldId === rowData.TargetFieldReferenceId) ?? null;
        if (currentCapabilities === null) {
            return false;
        }
        let source = currentCapabilities.Capabilities.find((e) => e.SourceFieldId === rowData.SourceFieldReferenceId) ?? null;
        return source !== null && source.TransformationLogicIds.indexOf(0) !== -1;
    }, [Capabilities]);

    const getOptionsDetailsForPopup = useCallback((type: number | null) =>
        TransformationLogic.GetOptionsDetails({ TransformationType: type } as SynchroFieldMappingDto, TransformationsCapabilities),
        [TransformationsCapabilities]);

    const getConstantFieldTypeForPopup = useCallback((type: number | null) =>
        TransformationLogic.GetConstantDetails({ TransformationType: type }, TransformationsCapabilities),
        [TransformationsCapabilities]);

    // Hook call kept unconditional (before the loading early-return below) per Rules of Hooks.
    const transformationColumn = useTransformationColumn({
        rowKeyField: 'TargetFieldReferenceId',
        typeField: 'TransformationType',
        parametersField: 'TransformationParameters',
        title: formatMessage("TransformationPopup_TitleTransformation"),
        transformationsCapabilities: TransformationsCapabilities,
        calculateAvailableTypes: calculateLookupTransformationTypes,
        getOptionsDetails: getOptionsDetailsForPopup,
        getConstantFieldType: getConstantFieldTypeForPopup,
        // Mandatory transformation: the picked source field can't map "Direct" (LogicId 0) to this target.
        isTransformationRequired: (rowData: SynchroFieldMappingDto) => !isDirectTransformationPossible(rowData),
        labelFormatter: (found) => found.Label,
        // AI "Combined" transformation: prompt popup instead of the standard type picker.
        complexTransformation: {
            logicId: 10001,
            getFieldLabel: (id: string) => SourceFields.find((f) => String(f.Id) === id || f.SourceKey === id)?.Name ?? id,
            onGenerate: async (rowData: SynchroFieldMappingDto, prompt: string) => {
                const result = await PathSynchroApi.GenerateAITransformationAsync(
                    PathId, TableReferenceSourceId, PathSourceId, TableReferenceTargetId, rowData.TargetFieldReferenceId, prompt);
                if (result.IsFailed()) {
                    throw new Error(result.ErrorMessage);
                }
                return result.Result;
            }
        },
        // Fold the live (pending) source-field selection into the row data the button/popup logic reads,
        // so the TRANSFORMATION button enables/populates right after a field is picked, before the save.
        resolveRowData: (cellData: any) => ({
            ...cellData.data,
            SourceFieldReferenceId: getEffectiveSourceFieldReferenceId(cellData)
        })
    });

    // useTransformationColumn() re-runs on every render regardless (its own popup state lives here),
    // so `transformationColumn` is a fresh object every time. Read it through a ref so the DataGrid
    // column props below can stay reference-stable across a popup-open re-render — otherwise every
    // column whose cellRender/calculateCellValue derives from it "changes" on each render and forces
    // DevExtreme to reconcile/repaint the whole grid before the popup even shows.
    const transformationColumnRef = useRef(transformationColumn);
    transformationColumnRef.current = transformationColumn;

    const transformationButtonCell = useCallback(
        (cellData: DataGridTypes.ColumnCellTemplateData) => transformationColumnRef.current.ButtonCell(cellData), []);
    const transformationCalculateCellValue = useCallback(
        (rowData: SynchroFieldMappingDto) => transformationColumnRef.current.calculateDisplayValue(rowData).trim().length > 0, []);
    const transformationCalculateSortValue = useCallback(
        (rowData: SynchroFieldMappingDto) => transformationColumnRef.current.calculateDisplayValue(rowData), []);

    const filterColumn = useFilterColumn({
        title: formatMessage("FilterColumn_Title"),
        rowKeyField: 'TargetFieldReferenceId',
        tableReferenceId: TableReferenceSourceId ?? 0,
        getRowFieldReferenceId: (rowData: SynchroFieldMappingDto) => rowData?.SourceFieldReferenceId ?? 0,
        getRowOriginFieldType: (rowData: SynchroFieldMappingDto) => rowData?.Type?.Type ?? 0,
        getPrefillTechnicalName: (rowData: SynchroFieldMappingDto) => StringHelper.ToLabelForApi(rowData?.Name ?? ""),
        // Synchro has no selection column, so hide the FILTER button on an unmapped row (no source
        // field linked) — a per-row filter there is meaningless. Read the live batch value (as the KEY
        // cell and the transformation button do) so it appears / disappears as fields are linked /
        // unlinked before a save.
        resolveRowData: (cellData: DataGridTypes.ColumnCellTemplateData) => ({
            ...cellData.data,
            SourceFieldReferenceId: getEffectiveSourceFieldReferenceId(cellData)
        }),
        isRowActive: (rowData: SynchroFieldMappingDto) => (rowData?.SourceFieldReferenceId ?? null) !== null,
        advancedMode: AdvancedFilterMode,
        onAdvancedModeChange: SetAdvancedFilterMode,
        advancedModeLocked: AdvancedFilterModeLocked
    });

    // Same reasoning as transformationColumnRef above: useFilterColumn()'s own popup state forces a
    // fresh `filterColumn` object on every render, even though its ButtonCell/HeaderCell/
    // calculateDisplayValue are internally memoized. Read it through a ref so this grid's own wrapper
    // functions keep stable identities too.
    const filterColumnRef = useRef(filterColumn);
    filterColumnRef.current = filterColumn;

    const filterButtonCell = useCallback(
        (cellData: DataGridTypes.ColumnCellTemplateData) => filterColumnRef.current.ButtonCell(cellData), []);
    // HeaderCell is memoised inside useFilterColumn and only changes identity when the checkbox state changes, so
    // passing it directly makes DevExtreme repaint the header with the current checked value.
    const filterHeaderCell = filterColumn.HeaderCell;
    const filterDisplayValue = useCallback(
        (rowData: SynchroFieldMappingDto) => filterColumnRef.current.calculateDisplayValue(rowData), []);

    // Same column either way; in "simple" mode it lives inside a FILTER band whose sub-header carries the
    // "Advanced mode" checkbox, otherwise it is a standalone group-header column.
    // The FILTER cell shows a button, not text: for the filter row we expose a boolean "configured or
    // not" so DevExtreme renders an "Avec / Sans" select instead of a free-text "contains" box.
    const filterIsConfigured = useCallback(
        (rowData: SynchroFieldMappingDto) => filterDisplayValue(rowData).trim().length > 0, [filterDisplayValue]);

    const filterColumnLeaf = React.useMemo(() => (
        <Column caption="FILTER" dataType="boolean" allowEditing={false} allowFiltering={true} allowSorting={false} width={210}
            cssClass={IsSimpleFilterMode ? "filter-cell-td" : "GridGroupHeader filter-cell-td"}
            headerCellRender={IsSimpleFilterMode ? filterHeaderCell : undefined}
            cellRender={filterButtonCell}
            calculateCellValue={filterIsConfigured}
            calculateSortValue={filterDisplayValue}
            trueText={formatMessage("Grid_ColumnFilterWith")}
            falseText={formatMessage("Grid_ColumnFilterWithout")}
        />
    ), [IsSimpleFilterMode, filterHeaderCell, filterButtonCell, filterIsConfigured, filterDisplayValue]);

    // Memoized (and kept before the early return, per Rules of Hooks) so the <Editing> element keeps a
    // stable identity: if it changes every render — as the override setState calls now trigger — devextreme-react
    // re-applies the editing config and wipes editing.changes, so the batch "modified" cell highlight
    // disappears mid-edit.
    const handleOnChangesChange = React.useCallback((e: DataChange[]): void => {
        SetMappingChanges(e, mappingRef.current);
    }, [SetMappingChanges]);

    const calculateLookupFields = useCallback((rowData: SynchroFieldMappingDto) => {
        return getFields(rowData.TargetFieldReferenceId, translatedSourceFields, Capabilities);
    }, [translatedSourceFields, Capabilities]);

    const handleOnEditorPreparing = useCallback((e: DataGridTypes.EditorPreparingEvent) => {

        const grid = e.component;

        if (e.parentType === 'dataRow' && e.dataField === 'SourceFieldReferenceId') {
            e.editorOptions.dataSource = calculateLookupFields(e.row!.data);

            e.editorOptions.onValueChanged = (args: any) => {

                const rowIndex = e.row!.rowIndex;

                // Transformation cleaning on type change
                if (args.previousValue > 0) {
                    let previousfield = translatedSourceFields.find((e: FieldReferenceIdAndKeyDto) => e.Id === args.previousValue) ?? null;
                    if (previousfield !== null) {
                        let newfield = translatedSourceFields.find((e: FieldReferenceIdAndKeyDto) => e.Id === args.value) ?? null;
                        if (newfield !== null) {
                            if (previousfield.Type.Type !== newfield.Type.Type) {
                                grid.cellValue(rowIndex, 'TransformationType', null);
                                grid.cellValue(rowIndex, 'TransformationParameters', "");
                            }
                        }
                    }
                }

                e.setValue(args.value);
            };
        }
    }, [calculateLookupFields, translatedSourceFields]);

    const handleSourceFieldReferenceIdCalculateDisplayValue = useCallback((e: any) => {
        let found = translatedSourceFields.find((x) => x.Id === e.SourceFieldReferenceId) ?? null;
        return found?.Name ?? "";
    }, [translatedSourceFields]);

    const sourceFieldTypeCell = useCallback((cellData: DataGridTypes.ColumnCellTemplateData) => {
        const effectiveSourceFieldReferenceId = getEffectiveSourceFieldReferenceId(cellData);

        let sourceField = translatedSourceFields.find((e) => e.Id === effectiveSourceFieldReferenceId) ?? null;

        if (sourceField !== null && sourceField.Id > 0) {
            return <FieldTypeTag FieldType={sourceField.Type} />;
        }

        return <div></div>;
    }, [translatedSourceFields, getEffectiveSourceFieldReferenceId]);

    const selectFieldCell = useCallback((cellData: DataGridTypes.ColumnCellTemplateData) => {
        // Same DevExtreme repaint gap as the unlink icon: the calculated displayValue doesn't refresh
        // reliably after cellValue() in batch edit mode, so read the live batch value from the grid.
        const effectiveSourceFieldReferenceId = getEffectiveSourceFieldReferenceId(cellData);
        if (effectiveSourceFieldReferenceId !== null && effectiveSourceFieldReferenceId !== undefined) {
            const found = translatedSourceFields.find((x) => x.Id === effectiveSourceFieldReferenceId) ?? null;
            return (<div style={{ textAlign: 'left' }}>{found?.Name ?? ""}</div>);
        }
        return (<div style={{ color: "#aaa", textAlign: 'left' }}>Select a field...</div>);
    }, [translatedSourceFields, getEffectiveSourceFieldReferenceId]);

    const UnlinkField = useCallback((cellData: DataGridTypes.ColumnCellTemplateData) => {
        const dataGrid = cellData.component;
        let rowIndex = cellData.row.rowIndex;
        if (cellData.data.IsKey) {
            dataGrid.cellValue(rowIndex, 'IsKey', false);
        }
        dataGrid.cellValue(rowIndex, 'SourceFieldReferenceId', null);
        dataGrid.cellValue(rowIndex, 'TransformationType', null);
        dataGrid.cellValue(rowIndex, 'TransformationParameters', "");
        // The unlink icon, source label / type and transformation button are calculated cellRender
        // columns: they re-read the (now cleared) batch values only if we repaint the row.
        dataGrid.repaintRows([rowIndex]);
    }, []);

    const renderUnlinkCommand = useCallback((cellData: DataGridTypes.ColumnCellTemplateData) => {
        const effectiveSourceFieldReferenceId = getEffectiveSourceFieldReferenceId(cellData);
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%' }}>
                {(effectiveSourceFieldReferenceId !== null && effectiveSourceFieldReferenceId !== undefined) ?
                    <img width={22} height={22} style={{ padding: '0px', cursor: 'pointer' }} src="svgs/unlink.svg" alt="Unlink" onClick={() => UnlinkField(cellData)} /> :
                    <img width={22} height={22} style={{ padding: '0px' }} src="svgs/link.svg" alt="link" />}
            </div>
        );
    }, [getEffectiveSourceFieldReferenceId, UnlinkField]);

    // Controlled checkbox instead of DevExtreme's always-on boolean editor: in batch mode that inline
    // editor re-registers the current value as a pending change on every row repaint, turning the KEY
    // cell "modified" (bordeaux) without any user action.
    // Disabled on a ReadOnly row and on an unlinked row (no source field): a key on an unmapped row
    // is meaningless and would be persisted as `{ sourceFieldReferenceId: 0, isKey: true }`.
    const renderKeyCell = useCallback((cellData: DataGridTypes.ColumnCellTemplateData) => {
        const linked = getEffectiveSourceFieldReferenceId(cellData) !== null;
        return (
            <CheckBox
                value={!!cellData.value}
                disabled={cellData.data.ReadOnly || !linked}
                onValueChanged={(e) => {
                    // e.event is only set for genuine user interaction; ignore programmatic value syncs.
                    if (e.event) {
                        // Never write through the rowIndex captured at render time: with infinite
                        // scrolling (row recycling) — and with any filter/sort that reorders the visible
                        // rows — it can point at another row by the time the checkbox is clicked, which
                        // flagged the wrong row as key while the clicked one reverted. Resolve the
                        // current visible index from the row key instead.
                        const rowIndex = cellData.component.getRowIndexByKey((cellData.data as SynchroFieldMappingDto).TargetFieldReferenceId);
                        if (rowIndex >= 0) {
                            cellData.component.cellValue(rowIndex, 'IsKey', e.value);
                        }
                    }
                }}
            />
        );
    }, [getEffectiveSourceFieldReferenceId]);

    const buildTreeViewDataSource = (fields: FieldReferenceIdAndKeyDto[]) => {
        const negativeIds = fields.filter(f => f.Id < 0);
        const positiveIds = fields.filter(f => f.Id >= 0);

        const treeData: any[] = [];

        if (negativeIds.length > 0) {
            treeData.push({
                Id: 'negative_group',
                Name: formatMessage("components_synchro-mapping-fields_FieldGrid_CategoryCalculated"),
                expanded: true,
                items: negativeIds
            });
        }

        if (positiveIds.length > 0) {
            treeData.push({
                Id: 'positive_group',
                Name: formatMessage("components_synchro-mapping-fields_FieldGrid_CategorySource"),
                expanded: true,
                items: positiveIds
            });
        }

        return treeData;
    };

    const SourceFieldTreeViewEditor = (props: { cellData: DataGridTypes.ColumnEditCellTemplateData }) => {
        const { cellData } = props;
        const availableFields = calculateLookupFields(cellData.data);
        const treeDataSource = buildTreeViewDataSource(availableFields);
        const [selectedValue, setSelectedValue] = useState(cellData.value);

        const handleValueChange = (newValue: number) => {
            const previousValue = selectedValue;

            // Transformation cleaning on type change
            if (previousValue > 0) {
                let previousfield = translatedSourceFields.find((e: FieldReferenceIdAndKeyDto) => e.Id === previousValue) ?? null;
                if (previousfield !== null) {
                    let newfield = translatedSourceFields.find((e: FieldReferenceIdAndKeyDto) => e.Id === newValue) ?? null;
                    if (newfield !== null) {
                        if (previousfield.Type.Type !== newfield.Type.Type) {
                            const grid = cellData.component;
                            const rowIndex = cellData.row.rowIndex;
                            grid.cellValue(rowIndex, 'TransformationType', null);
                            grid.cellValue(rowIndex, 'TransformationParameters', "");
                            grid.repaintRows([rowIndex]);
                        }
                    }
                }
            }

            setSelectedValue(newValue);
            cellData.setValue(newValue);

            // Auto-sélection du type de transformation uniquement s'il existe une seule transformation
            // et que "Direct" (Id=0) n'est pas une option possible.
            const updatedRowData = { ...cellData.data, SourceFieldReferenceId: newValue };
            const availableTransformations = calculateLookupTransformationTypes(updatedRowData);
            if (availableTransformations.length === 1 && !isDirectTransformationPossible(updatedRowData)) {
                const autoId = availableTransformations[0].Id;
                const grid = cellData.component;
                const rowIndex = cellData.row.rowIndex;
                grid.cellValue(rowIndex, 'TransformationType', autoId);
                grid.cellValue(rowIndex, 'TransformationParameters', "");
                grid.repaintRows([rowIndex]);
            }

            // Close the edit cell so the batch change is committed. Previously done implicitly by
            // navigating to the next column with grid.editCell(); that column is no longer editable
            // (it's the transformation button), so if we stay in edit mode saveEditData() flushes the
            // still-open custom editor as undefined and SourceFieldReferenceId is persisted as null.
            // Repaint the row afterwards so the sibling calculated columns (unlink icon, source
            // label / type, transformation button) pick up the staged SourceFieldReferenceId.
            setTimeout(() => {
                cellData.component.closeEditCell();
                cellData.component.repaintRows([cellData.row.rowIndex]);
            }, 0);
        };

        return (
            <DropDownBox
                value={selectedValue}
                dataSource={availableFields}
                displayExpr="Name"
                valueExpr="Id"
                placeholder="Select a field..."
                showClearButton={false}
                stylingMode="outlined"
                className="source-field-dropdown"
                dropDownOptions={{
                    width: 400,
                    // Height adapts to the list, capped so a long list scrolls instead of filling the
                    // screen; the cap also gives DevExtreme's flip collision a bounded size so the popup
                    // opens upward when near the bottom of the page.
                    height: 'auto',
                    maxHeight: 340
                }}
                onValueChanged={(e) => {
                    if (e.value !== undefined && e.value !== null) {
                        handleValueChange(e.value);
                    }
                }}
                contentRender={(dropDownOptions) => (
                    <TreeView
                        dataSource={treeDataSource}
                        dataStructure="tree"
                        height="100%"
                        keyExpr="Id"
                        displayExpr="Name"
                        parentIdExpr={undefined}
                        itemsExpr="items"
                        searchEnabled={true}
                        selectByClick={true}
                        selectionMode="single"
                        showCheckBoxesMode="none"
                        selectNodesRecursive={false}
                        selectedExpr="selected"
                        onContentReady={(e) => {
                            // Désélectionner tous les éléments d'abord
                            e.component.unselectAll();
                            // Sélectionner l'élément actuellement choisi
                            if (selectedValue !== null && selectedValue !== undefined) {
                                e.component.selectItem(selectedValue);
                            }
                        }}
                        onItemClick={(e) => {
                            if (e.itemData && !e.itemData.items) {
                                dropDownOptions.component.option('value', e.itemData.Id);
                                dropDownOptions.component.close();
                            }
                        }}
                    />
                )}
            />
        );
    };

    const renderSourceFieldTreeView = (cellData: DataGridTypes.ColumnEditCellTemplateData) => {
        return <SourceFieldTreeViewEditor cellData={cellData} />;
    };

    const handleApplyAutoMapping = useCallback((): void => {

        const icon = document.querySelector("#btn-auto-mapping .dx-icon");

        if (icon?.classList.contains("btn-spinning")) {
            return;
        }

        icon?.classList.add("btn-spinning");

        PathSynchroApi.GetPathSynchroCapabilitiesAsync(TableReferenceSourceId, PathSourceId, TableReferenceTargetId ?? -1, true).then((result) => {
            if (result.IsFailed()) {
                alert("Error : " + result.ErrorMessage);
                return;
            }

            let AutoMappingResult = result.Result.AutoMappingResult;

            if (AutoMappingResult === null || AutoMappingResult === "") {
                return;
            }

            let autoMappingCollection: any[] = [];
            try {
                autoMappingCollection = JSON.parse(AutoMappingResult);
            } catch {
                return;
            }

            if (!dataGridRef.current) {
                return;
            }

            let dataGrid = dataGridRef.current.instance();

            const touchedRows: number[] = [];

            autoMappingCollection.forEach((e: any) => {
                let rowIndex = dataGrid.getRowIndexByKey(e.TargetId) ?? -1;

                if (rowIndex >= 0) {

                    const rowData = dataGrid.getDataSource().items().find((item: SynchroFieldMappingDto) => item.TargetFieldReferenceId === e.TargetId) as SynchroFieldMappingDto;

                    // Auto mapping never picks a read-only target: it would need to be flagged as key.
                    if (rowData && !rowData.ReadOnly && !rowData.Type.IsReadOnly) {
                        dataGrid.cellValue(rowIndex, 'SourceFieldReferenceId', e.SourceId);
                        touchedRows.push(rowIndex);
                    }
                }
            });

            // Sibling calculated columns (unlink icon, source label / type, transformation button)
            // don't repaint on a cellValue() write to another column — force it.
            if (touchedRows.length > 0) {
                dataGrid.repaintRows(touchedRows);
            }

            icon?.classList.remove("btn-spinning");
        });
    }, [TableReferenceSourceId, PathSourceId, TableReferenceTargetId]);

    const renderColumnHeader = useCallback(() => (
        <div style={{ display: 'inline-flex', alignItems: 'center', width: '100%' }}>
            <Button
                elementAttr={{ id: "btn-auto-mapping" }}
                visible={sessionDetail?.WithAutoMapping}
                icon="sparkle"
                stylingMode="text"
                hint="Auto mapping"
                onClick={(e) => {
                    e.event?.stopPropagation();
                    handleApplyAutoMapping();
                }}
            />
        </div>
    ), [sessionDetail?.WithAutoMapping, handleApplyAutoMapping]);

    const handleOnEditingStart = useCallback((e: DataGridTypes.EditingStartEvent) => {
        e.cancel = e.data.ReadOnly;
    }, []);

    const handleOnRowPrepared = useCallback((e: DataGridTypes.RowPreparedEvent) => {
        if (e.rowType === "data" && e.data.ReadOnly) {
            e.rowElement.style.opacity = "0.5";
        }
    }, []);

    if (!SourceFields || !TargetFields || !Capabilities) {
        return <GlobalLoader />
    }

    return (
        <div className={"block-card datagrid-without-header"}>
            <GridBody
                dataGridRef={dataGridRef}
                dataSource={dataSourceRef.current!}
                onEditorPreparing={handleOnEditorPreparing}
                onEditingStart={handleOnEditingStart}
                onRowPrepared={handleOnRowPrepared}
                onEditingChangesChange={handleOnChangesChange}
                renderUnlinkCommand={renderUnlinkCommand}
                renderColumnHeader={renderColumnHeader}
                handleSourceFieldReferenceIdCalculateDisplayValue={handleSourceFieldReferenceIdCalculateDisplayValue}
                selectFieldCell={selectFieldCell}
                renderSourceFieldTreeView={renderSourceFieldTreeView}
                sourceFieldTypeCell={sourceFieldTypeCell}
                transformationButtonCell={transformationButtonCell}
                transformationCalculateCellValue={transformationCalculateCellValue}
                transformationCalculateSortValue={transformationCalculateSortValue}
                renderKeyCell={renderKeyCell}
                filterColumnLeaf={filterColumnLeaf}
                FiltersEnabled={FiltersEnabled}
                IsSimpleFilterMode={IsSimpleFilterMode}
            />

            {transformationColumn.PopupElement}
            {FiltersEnabled && filterColumn.PopupElement}
        </div>
    );
});

export default FieldGrid;
